
import { Link, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import {
  LayoutDashboard,
  PenTool,
  Image,
  Mail,
  Users,
  MessageSquare,
  Database,
  ChevronDown,
  FileText,
  Plus,
  Sparkles,
  ImageIcon,
  Palette,
  BarChart3,
  Brush,
  File,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export const AdminNavigationMenu = () => {
  const location = useLocation();

  const isActive = (path: string) => location.pathname === path;
  const isActiveDropdown = (paths: string[]) => paths.some(path => location.pathname === path);

  const navItems = [
    { name: "Dashboard", href: "/admin", icon: LayoutDashboard },
    { name: "Contact", href: "/admin/contact-submissions", icon: Mail },
    { name: "Membership", href: "/admin/membership-applications", icon: Users },
    { name: "Survey Results", href: "/admin/survey-results", icon: BarChart3 },
    { name: "Chat History", href: "/admin/chat-history", icon: MessageSquare },
    { name: "Knowledge Base", href: "/admin/knowledge-base", icon: Database },
  ];

  const emailDropdownItems = [
    { name: "Email Builder", href: "/admin/email-builder", icon: Brush },
    { name: "Templates", href: "/admin/email-templates", icon: File },
  ];

  const articlesDropdownItems = [
    { name: "Articles", href: "/admin/articles/list", icon: FileText },
    { name: "New Article", href: "/admin/articles", icon: Plus },
    { name: "AI Articles", href: "/admin/articles/ai", icon: Sparkles },
  ];

  const imagesDropdownItems = [
    { name: "Gallery", href: "/admin/gallery", icon: ImageIcon },
    { name: "Image Studio", href: "/admin/image-studio", icon: Palette },
  ];

  return (
    <nav className="flex-1">
      <div className="flex flex-wrap gap-2">
        {navItems.map((item) => {
          const Icon = item.icon;
          return (
            <Link
              key={item.name}
              to={item.href}
              className={cn(
                "inline-flex h-10 items-center justify-center rounded-md px-4 py-2 text-sm font-medium transition-colors hover:bg-blue-700 hover:text-blue-100 focus:bg-accent focus:text-accent-foreground focus:outline-none disabled:pointer-events-none disabled:opacity-50 text-white",
                isActive(item.href) && "bg-blue-700"
              )}
            >
              <Icon className="h-4 w-4 mr-2" />
              {item.name}
            </Link>
          );
        })}

        {/* Articles Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className={cn(
                "inline-flex h-10 items-center justify-center rounded-md px-4 py-2 text-sm font-medium transition-colors hover:bg-blue-700 hover:text-blue-100 focus:bg-accent focus:text-accent-foreground focus:outline-none disabled:pointer-events-none disabled:opacity-50 text-white",
                isActiveDropdown(["/admin/articles", "/admin/articles/list", "/admin/articles/ai"]) && "bg-blue-700"
              )}
            >
              <PenTool className="h-4 w-4 mr-2" />
              Articles
              <ChevronDown className="h-4 w-4 ml-1" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="bg-white border border-gray-200 shadow-lg z-50">
            {articlesDropdownItems.map((item) => {
              const Icon = item.icon;
              return (
                <DropdownMenuItem key={item.name} asChild>
                  <Link
                    to={item.href}
                    className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
                  >
                    <Icon className="h-4 w-4 mr-2" />
                    {item.name}
                  </Link>
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Email Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className={cn(
                "inline-flex h-10 items-center justify-center rounded-md px-4 py-2 text-sm font-medium transition-colors hover:bg-blue-700 hover:text-blue-100 focus:bg-accent focus:text-accent-foreground focus:outline-none disabled:pointer-events-none disabled:opacity-50 text-white",
                isActiveDropdown(["/admin/email-builder", "/admin/email-templates"]) && "bg-blue-700"
              )}
            >
              <Mail className="h-4 w-4 mr-2" />
              Email
              <ChevronDown className="h-4 w-4 ml-1" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="bg-white border border-gray-200 shadow-lg z-50">
            {emailDropdownItems.map((item) => {
              const Icon = item.icon;
              return (
                <DropdownMenuItem key={item.name} asChild>
                  <Link
                    to={item.href}
                    className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
                  >
                    <Icon className="h-4 w-4 mr-2" />
                    {item.name}
                  </Link>
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Images Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className={cn(
                "inline-flex h-10 items-center justify-center rounded-md px-4 py-2 text-sm font-medium transition-colors hover:bg-blue-700 hover:text-blue-100 focus:bg-accent focus:text-accent-foreground focus:outline-none disabled:pointer-events-none disabled:opacity-50 text-white",
                isActiveDropdown(["/admin/gallery", "/admin/image-studio"]) && "bg-blue-700"
              )}
            >
              <Image className="h-4 w-4 mr-2" />
              Images
              <ChevronDown className="h-4 w-4 ml-1" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="bg-white border border-gray-200 shadow-lg z-50">
            {imagesDropdownItems.map((item) => {
              const Icon = item.icon;
              return (
                <DropdownMenuItem key={item.name} asChild>
                  <Link
                    to={item.href}
                    className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
                  >
                    <Icon className="h-4 w-4 mr-2" />
                    {item.name}
                  </Link>
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </nav>
  );
};
